/*
  /women トップを女性向けのトーンにした版 (2026-10-19 代表「基本的には今の HP 構成で OK。その上で、デザイントンマナを変える」)。
  節の並び、文言、画像、部品の構造は変えない。色、字、角、影、線、地、写真の焼き方だけをここで差し替える。

  色は女性向けキャリアスクールの HP のトーン (白と淡い桜色の地、灰色の字、桜色のマーカー) から拾った値。
  ボタンの赤 #C2504B は拾った値をやめて自分たちで決めた (白い字との比 4.62、下の縁 #973A36、hover #AD4743、押した #A4433F)。赤を字に使う所は 1 段濃い #AD4743。
  拾った場所は notes-sandbox/ops/scratch/2026-10-19/hp-women-tone/tokens.md にある。他社のロゴ、写真、文言、札の形は使っていない。

  効かせる範囲は .hp.hp-home だけ (トップと広告用の写し /women/lp)。料金、受講生の声、支払いの頁は .hp-home を持たないので変わらない。
  ヘッダーとフッターは .hp の外なので html:has(.hp-home) で当てる。
  色の値は下の変数の束だけに置く。節ごとの指定は変数だけを使う
  見出しの Zen Kaku Gothic New と手書きの Klee One は lib/deferred-google-fonts.ts が load の後に足す (描画を止めない)
*/

/** --w-* の値の束。LP 群 (pages/lp/women-tone.ts) も同じ値を読む */
export const WOMEN_TONE_VARS = `
  --w-bg:#FFFFFF;--w-blush:#FBF7F7;--w-blush-2:#F9F2F2;--w-blush-3:#F4E6E6;
  --w-ink:#333333;--w-head:#1F2D32;--w-sub:#5E5B5C;--w-note:#5A6066;
  --w-rose:#D07C7C;--w-rose-ink:#A24E55;--w-rose-soft:#E7A8B1;--w-mark:#F7D7DB;
  --w-cta:#C2504B;--w-cta-edge:#973A36;--w-cta-hover:#AD4743;--w-cta-press:#A4433F;--w-cta-ink:#AD4743;
  --w-navy:#315F78;--w-navy-2:#284F64;--w-teal:#74B6A9;--w-aqua:#E0EFF3;
  --w-ground-alt:#FDF9FA;--w-ground-line:#F1E4E6;
  --w-grad-pink:#F3CED7;--w-grad-lilac:#E6D4E7;--w-grad-sky:#D0DEF0;
  --w-show-grad:linear-gradient(120deg,var(--w-grad-pink) 0%,var(--w-grad-lilac) 52%,var(--w-grad-sky) 100%);
  --w-end-wash:linear-gradient(180deg,rgba(250,240,242,.88),rgba(234,237,246,.94));
  --w-show-grad-v:linear-gradient(180deg,var(--w-grad-pink) 0%,var(--w-grad-lilac) 52%,var(--w-grad-sky) 100%);
  --w-rose-deep:#92404A;--w-sub-deep:#4F4C4D;--w-note-deep:#4A5258;
  --w-band-dim:#4E5C63;--w-band-rule:rgba(49,95,120,.22);--w-band-edge:#F6E3E7;
  --w-rule:#EADCDC;--w-shadow:0 6px 20px rgba(160,96,104,.10);
  --w-r:16px;--w-r-s:10px;
  --w-hand:"Klee One",var(--gothic);
  --w-head-font:"Zen Kaku Gothic New","Women FV Sans","Noto Sans JP",var(--font-sans-ja);
`;

const TOKENS = `
.hp.hp-home{${WOMEN_TONE_VARS}

  --ink:var(--w-ink);--sub:var(--w-sub);
  --black:var(--w-navy);--yellow:var(--w-mark);--ymark:var(--w-mark);
  --cream:var(--w-blush);--kinari:var(--w-blush);
  --blue:var(--w-rose-ink);--blue-wash:var(--w-blush);--blue-soft:var(--w-rose-soft);
  --brass:var(--w-rose);--brass-l:var(--w-mark);
  --red:var(--w-cta);--line:var(--w-rule);--rule:var(--w-rule);
  --sky:var(--w-rose);--sky-wash:var(--w-blush);--sky-d:var(--w-blush-2);
  --mincho:var(--w-head-font);
  --grad:radial-gradient(60% 80% at 10% 10%,#FBE1E4 0,transparent 70%),radial-gradient(60% 70% at 90% 90%,var(--w-aqua) 0,transparent 70%),linear-gradient(#fff,#fff);
  --focus-ring:var(--w-cta);
  letter-spacing:.04em;
}
/* 最初の画面は淡い地になったので、focus の輪は白ではなくボタンの下の縁の赤 (白い輪は地に溶けて見えない) */
.hp.hp-home :is(.hx-dark,.hx-blue){--focus-ring:#fff}
.hp.hp-home .hp-fv{--focus-ring:var(--w-cta-edge)}
`;

/* 地。生成りの方眼をやめ、白と淡い桜色を交互に */
const GROUND = `
.hp.hp-home .hx-cream{background-image:none}
.hp.hp-home .hx-cream::before,.hp.hp-home .hx-cream::after{content:none}
.hp.hp-home :is(.hx-dark,.hx-blue){background:radial-gradient(70% 60% at 100% 0,rgba(208,124,124,.28) 0,transparent 70%),linear-gradient(160deg,var(--w-navy) 0,var(--w-navy-2) 100%)}
`;

/* 字。見出しは角のあるゴシックの太字で字間を広く。本文は字間と行間を少し開ける */
const TYPE = `
.hp.hp-home :is(.hx-h2,.hx-h2.hx-bands,.hx-h2.hx-strike,section h2){color:var(--w-head);letter-spacing:.08em;font-weight:700}
.hp.hp-home :is(.hx-dark,.hx-blue,.bp,.hx-pband) :is(.hx-h2,h2,.bp-h,.hx-pband-h){color:#fff}
.hp.hp-home .bp{--font-body-ja:var(--gothic)}
.hp.hp-home .bp .bp-h{--font-body-ja:var(--w-head-font);letter-spacing:.08em}
.hp.hp-home .hx-sec{line-height:1.9}
/* 字間を広げると見出しの最後の行に 1〜2 字だけ落ちる (「ド」「読む」「4本」) ので、文節で折って行の長さをそろえる。
   狭い列の経歴 (dd) は字間を戻し、文の中の「手に入るもの」のリンクは割らない */
.hp.hp-home :is(h3,h4){letter-spacing:.02em;word-break:auto-phrase;text-wrap:balance}
.hp.hp-home :is(dd,dt){letter-spacing:0}
.hp.hp-home .px-pc-body h3{letter-spacing:0}
.hp.hp-home .hx-incl-sum .hx-tlink{white-space:nowrap}
.hp.hp-home .hx-k{--font-body-ja:var(--gothic);font-weight:500;letter-spacing:.2em;color:var(--w-rose-ink)}
.hp.hp-home :is(.hx-dark,.hx-blue) .hx-k{color:var(--w-mark)}
.hp.hp-home p.tracking-\\[0\\.22em\\]{--font-body-ja:var(--gothic);font-weight:500;letter-spacing:.2em;color:var(--w-rose-ink)}
.hp.hp-home p.tracking-\\[0\\.22em\\]::before{height:2px;border-radius:2px;background:var(--w-rose)}
.hp.hp-home .hx-note{color:var(--w-note)}
/* 字間を広げた分、講師の条件の文が「と/しての」で割れるので、<wbr> の句の切れ目だけで折る */
.hp.hp-home .hx-i-crit-t{word-break:keep-all}
`;

/* マーカー。真鍮の細い下線を、桜色の太いマーカーに。黒い面の上は白の字に淡い桜の下線 */
const MARK = `
.hp.hp-home :is(.hx-hl,.px-em,.em mark,.hx-qi strong,.hx-vc-full strong,.vd-a mark,.pp-vs-mk),.hp.hp-home .pp-pt td.yes .pp-pt-pr{background:linear-gradient(transparent 60%,var(--w-mark) 60%);padding-bottom:0}
.hp.hp-home :is(.hx-dark,.hx-blue) :is(.hx-hl,.px-em){background:linear-gradient(transparent 62%,rgba(231,168,177,.55) 62%)}
.hp.hp-home .hx-about-lede b{background:linear-gradient(transparent 60%,var(--w-mark) 60%);padding-bottom:0}
.hp.hp-home .hx-bands>span{background:linear-gradient(transparent 58%,var(--w-mark) 58%);padding:2px .25em 2px}
html.hx-motion .hp.hp-home .hx-head-c[data-mo] .hx-bands>span::before{top:58%;height:auto;bottom:0;background:var(--w-mark);z-index:-1}
.hp.hp-home .hx-head-c[data-mo] .hx-bands>span{position:relative;z-index:0}
.hp.hp-home .hp-ins h2 .text-shegate-accent{background:linear-gradient(transparent 58%,var(--w-mark) 58%)}
.hp.hp-home .hx-flag{background:var(--w-rose-ink);color:#fff;border-radius:999px;clip-path:none;padding:4px 18px;font-weight:700;letter-spacing:.08em}
.hp.hp-home :is(.hx-dark,.hx-blue) .hx-flag{background:#fff;color:var(--w-rose-ink)}
.hp.hp-home .hx-strike del{color:#6B6B74}
`;

/* 角と影。札と箱は丸く、影は桜色を帯びた柔らかい 1 段だけ */
const SHAPE = `
.hp.hp-home :is(
  .em-ng,.em-no,.em-card,.em-ic,.em-chip,.em-k,.em-down,.em-dv-n,.em-it,
  .hx-frame,.hx-pd-tab,.hx-pd-shot,.px-pc,.px-pc-ico,.px-grp,.px-rt,.px-app,
  .hx-pdt-plate,.hx-i-photo,.hx-vc,.hx-vc-pm,.hx-vf-box,.vw-c,
  .hx-wyg-card,.hx-wyg-ic,.hx-wyg-list,.hx-wyg-boxin,
  .pp-pt-frame,.pp-pay-card,.pp-sim,.pp-sim-plan,.pp-sim-val,.pp-pay-note,.pp-vs-frame,
  .hx-grant,.hx-end-step,.hx-end-opt
){border-radius:var(--w-r)}
.hp.hp-home :is(.em-chip,.px-chips>span,.px-grp-tag,.hx-i-role,.hx-i-topics>span,.hx-pd-stack>span,.hx-wyg-cat,.pp-pt-flag,.pp-vs-flag,.pp-sim-chip,.hx-pb-nm em,.hx-end-no,.hx-wyg-meter>span,.em-drop-track,.em-drop-track>i){border-radius:999px}
.hp.hp-home :is(.pp-sim-seg,.pp-sim-seg>button,.pp-sim-step>button,.hx-vf-box button,.hx-i-crit-l>li,.hx-grant-flow>li){border-radius:var(--w-r-s)}
.hp.hp-home .hx-pb-a{border-radius:var(--w-r-s) 0 0 var(--w-r-s)}
.hp.hp-home .hx-pb-a:last-child{border-radius:var(--w-r-s)}
.hp.hp-home .hx-pb-b{border-radius:0 var(--w-r-s) var(--w-r-s) 0}
/* 生涯サポートの帯の先の矢印。枠の線の色にせず、帯の面と同じ桜色の三角にして、帯の右の角は丸めない */
.hp.hp-home .hx-pb-arrow{border-left-color:var(--w-mark)}
.hp.hp-home .hx-pb-b:has(+.hx-pb-arrow){border-radius:0}
.hp.hp-home .hx-end-pic{border-radius:var(--w-r) var(--w-r) 0 0}
.hp.hp-home .hx-end-step:nth-child(5) .hx-end-pic{border-radius:var(--w-r) 0 0 var(--w-r)}
@media (min-width:900px){.hp.hp-home .hx-end-step:nth-child(5) .hx-end-pic{border-radius:var(--w-r) var(--w-r) 0 0}}
.hp.hp-home :is(.rounded-2xl,.rounded-3xl){border-radius:var(--w-r)}
.hp.hp-home .rounded-xl{border-radius:var(--w-r-s)}
.hp.hp-home .rounded-t-2xl{border-radius:var(--w-r) var(--w-r) 0 0}
.hp.hp-home .rounded-b-2xl{border-radius:0 0 var(--w-r) var(--w-r)}
.hp.hp-home .hx-frame{border-color:var(--w-rule);box-shadow:var(--w-shadow)}
.hp.hp-home .hx-frame-bar{border-bottom-color:var(--w-rule);background:var(--w-blush)}
.hp.hp-home .hx-frame-bar i{border-color:var(--w-rose-soft)}
.hp.hp-home :is(.em-card,.hx-wyg-card,.pp-pay-card,.pp-sim,.hx-vc){box-shadow:var(--w-shadow)}
.hp.hp-home .hx-pull{border-top-color:var(--w-rose-soft)}
.hp.hp-home .hx-qi{border-bottom-color:var(--w-rule)}
.hp.hp-home .hx-qi-tp::before{border-radius:50%}
`;


/* 線。墨の細い枠と罫を、桜色を帯びた淡い灰の線に。主役の列の枠だけ紺 */
const LINES = `
.hp.hp-home :is(.border-shegate-lime-ink,.em-b-hi,.em-card,.fq,.fq-item,.hx-arr,.hx-b-wide,.hx-frame,.hx-frame-bar,.hx-frame-bar i,.hx-grant,.hx-grant-flow li:nth-child(odd),.hx-i-crit,.hx-i-crit-l li,.hx-i-photo,.hx-incl-sum,.hx-kd,.vw-c,.hx-pb-track>div:not(.hx-pb-arrow),.hx-pd-ch,.hx-pd-shot,.hx-pd-shot figcaption,.hx-pd-stack span,.hx-pd-tab,.hx-pd-tabs,.hx-pd-total,.hx-pdt-close,.hx-pdt-dhead,.hx-pdt-tip li,.hx-pdt-tips,.hx-pull,.hx-dt,.hx-scope-cards li,.hx-scope-short,.hx-scope-tags i,.hx-span-tr,.hx-span-tr>span,.hx-vc,.hx-vc-art,.hx-vc-pm,.hx-vf-hd,.hx-vnums li,.hx-w-r>.hx-qi:first-child,.hx-wyg-card,.hx-wyg-cat-sp,.hx-wyg-list,.k1-n,.pp-aud-row,.pp-facts li,.pp-ft,.pp-list h3,.pp-pay-card,.pp-pay-note,.pp-pts,.pp-sim,.pp-sim-seg,.pp-sim-chip,.pp-sim-out,.pp-sim-step button,.pp-sim-val,.px-pc,.px-rt,.px-note,.px-close,.px-app,.px-app-screens img,.px-chip,.px-goal-logos span,.px-grp,.px-mq-chip,.px-mq-row,.px-pc-foot,.px-pc-ico,.px-pc-top,.px-rt-media,.px-rt-q>div){border-color:var(--w-rule)}
/* 他の学校の期間の線は、淡い罫の色だと地に溶けて長さが読めないので、今の灰と同じ濃さの桜灰に */
.hp.hp-home .hx-scope-row .hx-scope-track .hx-scope-short{border-top-color:#C4A9AC}
.hp.hp-home :is(.pp-pt-frame,.pp-vs-frame,.pp-pt td.yes,.pp-vs td.yes,.pp-pt thead th.yes,.pp-vs thead th.yes){border-color:var(--w-navy)}
`;

/* 塗り。墨で塗っていた箱は紺、小さな札と番号は深い桜色。字は白のまま */
const FILLS = `
.hp.hp-home :is(.em-b-base,.em-do .em-it,.em-lg-all,.hx-kd-h,.hx-lv-p,.hx-pb-a,.hx-pd-tab[aria-current=true],.hx-wyg-boxin,.pp-pay-tag,.pp-pt thead th.yes,.pp-sim-h,.pp-sim-seg [aria-checked="true"],.pp-vs thead th.yes){background-color:var(--w-navy)}
.hp.hp-home :is(.em-ng,.em-no,.pp-pay-ni,.pp-pt-ck,.px-grp-tag){background-color:var(--w-rose-ink)}
.hp.hp-home .pp-sim-l::before{background:var(--w-rose-ink)}
.hp.hp-home .hx-vc-pm::before,.hp.hp-home .hx-vc-pm::after{background:var(--w-rose-ink)}
@media (min-width:768px){.hp.hp-home .md\\:bg-\\[\\#1a1a1a\\]\\/92{background-color:rgba(49,95,120,.94)}}
.hp.hp-home .card-open-hint{background:rgba(49,95,120,.92)}
/* 黒い言い切りの帯 (.bp) とフッターも紺のにじみに */
.hp.hp-home .bp{--bp-y:var(--w-mark);background:radial-gradient(70% 60% at 100% 0,rgba(208,124,124,.28) 0,transparent 70%),linear-gradient(160deg,var(--w-navy) 0,var(--w-navy-2) 100%)}
.hp.hp-home [fill="#FFE566"]{fill:var(--w-mark)}
.hp.hp-home .npb-pr1{fill:#FFE566}
.hp.hp-home .pp-pay-fig rect[fill="#0E0E10"]{fill:var(--w-navy)}
.hp.hp-home .pp-pay-fig text[fill="#0E0E10"]{fill:var(--w-head)}
.hp.hp-home .pp-pay-fig [stroke="#0E0E10"]{stroke:var(--w-navy)}
.hp.hp-home :is(.pp-vs,.pp-pt) tbody th{background:var(--w-blush-3)}
/* 締めの写真の暗いぼかしを紺に。写真の上は薄く */
.hp.hp-home .hx-end-photo::after{background:linear-gradient(180deg,rgba(40,79,100,0) 45%,rgba(40,79,100,.85) 100%)}
/* PC は明るい写真の左と下の縁が紺の地に四角く浮くので、重ねの色ではなく写真そのものを左と下で透かして地に溶かす */
@media (min-width:900px){.hp.hp-home .hx-end-photo{-webkit-mask-image:linear-gradient(90deg,transparent 0,#000 38%),linear-gradient(0deg,transparent 0,#000 30%);-webkit-mask-composite:source-in;mask-image:linear-gradient(90deg,transparent 0,#000 38%),linear-gradient(0deg,transparent 0,#000 30%);mask-composite:intersect}.hp.hp-home .hx-end-photo::after{background:none}}
/* 紙の束の帯。白黒の写真は紺の地に溶かして、紺を帯びた写真にする */
.hp.hp-home .hx-pband{background:linear-gradient(160deg,var(--w-navy) 0,var(--w-navy-2) 100%)}
.hp.hp-home .hx-pband-photo img{object-position:50% 62%}
@media (min-width:1024px){.hp.hp-home .hx-pband-photo img{object-position:50% 78%}}
.hp.hp-home .hx-pband-credit{position:absolute;z-index:2;right:10px;bottom:8px;margin:0;font-size:11px;line-height:1.4;color:rgba(255,255,255,.88);writing-mode:horizontal-tb;--font-body-ja:var(--gothic);font-weight:400}
/* 締めの面。にじむ地 (Adobe Stock 1705386134、青から赤) に紺を重ねて白い字を読ませる */
.hp.hp-home .hx-end{background:linear-gradient(180deg,rgba(40,79,100,.62),rgba(40,79,100,.8)),url(/assets/stock/as1705386134-grad.webp) center/cover,var(--w-navy)}
.hp.hp-home .hx-end-top{position:relative}
.hp.hp-home .hx-end-credit{position:absolute;z-index:2;right:12px;top:8px;margin:0;font-size:11px;line-height:1.4;color:var(--w-sub);text-shadow:0 0 6px #fff,0 0 2px #fff}
@media (min-width:900px){.hp.hp-home .hx-end-credit{right:max(12px,calc(50% - 628px))}}
.hp.hp-home .hx-pband-photo::after{background:linear-gradient(to right,var(--w-navy) 0,rgba(49,95,120,.55) 14%,rgba(49,95,120,0) 36%),linear-gradient(to bottom,rgba(40,79,100,.3) 0,rgba(40,79,100,0) 20%,rgba(40,79,100,0) 80%,rgba(40,79,100,.5) 100%)}
/* 紹介動画の再生の札と、下に貼り付く帯 */
.hp.hp-home .hx-prv-btn{background:var(--w-cta);box-shadow:0 4px 0 var(--w-cta-edge)}
.hp.hp-home .border-\\[\\#121417\\]{border-color:var(--w-rule)}
.hp.hp-home a.bg-\\[\\#121417\\]{background:var(--w-cta);border-radius:999px;box-shadow:0 3px 0 var(--w-cta-edge)}
.hp.hp-home a.bg-\\[\\#121417\\]:hover{background:var(--w-cta-hover)}
.hp.hp-home .decoration-\\[\\#1F56D6\\]{text-decoration-color:var(--w-rose)}
.hp.hp-home .text-\\[\\#121417\\]{color:var(--w-head)}
`;

/* ボタン。深い赤の丸い札に白い字、下の縁だけ濃い赤。LINE の緑のボタンはそのまま */
const BUTTON = `
.hp.hp-home :is(.hx-pill-onLight,.hx-pill-onDark,.hp-fv-btn,.bg-shegate-lime){background:var(--w-cta);color:#fff;border-radius:999px!important;box-shadow:0 4px 0 var(--w-cta-edge);transition:background-color var(--m-cta) var(--m-out),transform var(--m-cta) var(--m-out),box-shadow var(--m-cta) var(--m-out)}
.hp.hp-home :is(.hx-pill-onLight,.hx-pill-onDark,.hp-fv-btn,.bg-shegate-lime):hover{background:var(--w-cta-hover);color:#fff}
.hp.hp-home :is(.hx-pill-onLight,.hx-pill-onDark,.hp-fv-btn,.bg-shegate-lime):active{background:var(--w-cta-press);transform:translateY(3px);box-shadow:0 1px 0 var(--w-cta-edge)}
.hp.hp-home :is(.hx-pill,.hp-fv-btn) :is(svg,.hp-fv-arrow,.hx-arrow){color:#fff}
.hp.hp-home .border-shegate-lime-ink{border-color:var(--w-cta);color:var(--w-cta-ink);border-radius:999px}
.hp.hp-home .border-shegate-lime-ink:hover{background-color:var(--w-cta);color:#fff}
.hp.hp-home :is(#women-faq-trigger-hero,#women-faq-trigger-mid-page){border-color:var(--w-rose);color:var(--w-head);border-radius:999px}
.hp.hp-home :is(#women-faq-trigger-hero,#women-faq-trigger-mid-page):hover{background:var(--w-blush-2)}
/* 下に貼り付く帯は、字間を広げると 390 幅で「まずは無料30日チャレンジか…」と切れるので字間を戻す */
.hp.hp-home .fixed.inset-x-0.bottom-0{letter-spacing:0}
.hp.hp-home .fixed.inset-x-0.bottom-0>.bg-\\[\\#1a1a1a\\]{border-color:var(--w-rule);border-radius:var(--w-r);box-shadow:var(--w-shadow)}
`;

/* 最初の画面。黒い地をやめ、桜色・珊瑚色・水色がにじむ地に。人の動画は明るく焼き直し、字の下は白くぼかす */
const FV = `
.hp.hp-home .hp-fv{--fv-ink:var(--w-head);--fv-dim:var(--w-sub);--mm-line:var(--w-cta);--mm-dim:#9E989A;color:var(--w-ink);
  background:
    radial-gradient(42% 30% at 0 52%,rgba(240,140,158,.85) 0,rgba(240,140,158,0) 75%),
    radial-gradient(40% 28% at 100% 60%,rgba(244,150,130,.7) 0,rgba(244,150,130,0) 75%),
    radial-gradient(60% 30% at 95% 100%,rgba(140,204,214,.9) 0,rgba(140,204,214,0) 75%),
    radial-gradient(50% 26% at 0 100%,rgba(250,200,190,.95) 0,rgba(250,200,190,0) 75%),
    radial-gradient(70% 40% at 50% 70%,#FFF8F7 0,rgba(255,248,247,0) 80%),
    url(/assets/stock/as671011641-grad.webp) center/cover,#FCEFF0}
@media (min-width:768px){
  .hp.hp-home .hp-fv{background:
    radial-gradient(34% 50% at 0 0,rgba(240,140,158,.9) 0,rgba(240,140,158,0) 75%),
    radial-gradient(30% 45% at 26% 100%,rgba(140,204,214,.85) 0,rgba(140,204,214,0) 75%),
    radial-gradient(30% 40% at 40% 40%,rgba(250,200,190,.7) 0,rgba(250,200,190,0) 75%),
    radial-gradient(40% 50% at 100% 100%,rgba(244,150,130,.55) 0,rgba(244,150,130,0) 75%),
    url(/assets/stock/as671011641-grad.webp) center/cover,#FCEFF0}
}
.hp.hp-home .hp-fv-credit{position:absolute;z-index:3;top:8px;right:12px;margin:0;font-size:11px;line-height:1.4;letter-spacing:.04em;color:var(--w-sub);text-shadow:0 0 6px #fff,0 0 2px #fff}
/* スマホは右上だと髪にかかって読めないので、窓の明るい左上に置く */
@media (max-width:767px){.hp.hp-home .hp-fv-credit{right:auto;left:12px}}
@media (min-width:768px){.hp.hp-home .hp-fv-credit{top:14px;right:20px}.hp.hp-home .hp-fv-bgimg{object-position:52% 40%}}
.hp.hp-home .hp-fv-bg::after{background:linear-gradient(180deg,rgba(252,239,240,0) 34%,rgba(252,239,240,.8) 62%,#FCEFF0 96%)}
.hp.hp-home :is(.hp-fv-h,.hp-fv-sub,.hp-fv-label,.hp-fv-num b,.hp-fv-num span,.hp-fv-cap strong){color:var(--w-head)}
.hp.hp-home .hp-fv-h{letter-spacing:.06em}
.hp.hp-home .hp-fv-label span{border-color:var(--w-rose);background:#fff;color:var(--w-rose-ink)}
.hp.hp-home :is(.hp-fv-lede,.hp-fv-price){color:var(--w-ink)}
.hp.hp-home .hp-fv-key{background:linear-gradient(transparent 55%,var(--w-mark) 55%);color:var(--w-cta-ink);padding:0 .08em}
.hp.hp-home .hp-fv-link{color:var(--w-head);text-decoration-color:var(--w-rose)}
.hp.hp-home .hp-fv-link:hover{text-decoration-color:var(--w-cta)}
.hp.hp-home .hp-fv-ic{--icon-accent:var(--w-mark);--icon-paper:#fff;color:var(--w-rose-ink)}
.hp.hp-home .hp-fv-facts{border-color:rgba(176,90,96,.22);background:rgba(255,255,255,.72);border-radius:var(--w-r);border-width:0;padding:16px 14px;box-shadow:var(--w-shadow)}
.hp.hp-home .hp-fv-fact:not(:nth-child(2n+1)){border-left-color:var(--w-rule)}
.hp.hp-home .hp-fv-sign span{--font-body-ja:var(--w-hand);font-family:var(--w-hand)!important;font-weight:600;font-size:22px;letter-spacing:.06em;color:var(--w-rose-ink);transform:rotate(-3deg);display:inline-block}
.hp.hp-home .hp-fv-sign img{filter:none}
/* 手書きの 2 か所目。30ヶ月の約束の一行 */
.hp.hp-home .bp-m30 .bp-h{--font-body-ja:var(--w-hand);font-family:var(--w-hand)!important;font-weight:600;letter-spacing:.06em}
@media (min-width:768px){
  .hp.hp-home .hp-fv-bgimg{-webkit-mask-image:linear-gradient(90deg,transparent 0,rgba(0,0,0,.35) 22%,rgba(0,0,0,.85) 44%,#000 58%);mask-image:linear-gradient(90deg,transparent 0,rgba(0,0,0,.35) 22%,rgba(0,0,0,.85) 44%,#000 58%)}
  .hp.hp-home .hp-fv-bg::after{background:linear-gradient(90deg,rgba(252,239,240,0) 0,rgba(252,239,240,0) calc(var(--fv-bg-l) + 30%)),linear-gradient(0deg,rgba(252,239,240,.95) 0,rgba(252,239,240,.4) 22%,rgba(252,239,240,0) 46%)}
  .hp.hp-home .hp-fv-fact:not(:nth-child(3n+1)){border-left-color:var(--w-rule)}
}
@media (min-width:1024px){
  .hp.hp-home .hp-fv-fact:nth-child(n){border-left-color:var(--w-rule)}
  .hp.hp-home .hp-fv-fact:first-child{border-left:0}
}
`;

/* 写真。白黒のフィルタを外し、字を重ねる帯の暗い重ねは浅くする */
const PHOTO = `
.hp.hp-home .hx-i-photo>img{filter:none}
.hp.hp-home .hx-i-photo{border-color:var(--w-rule)}
/* 講師の写真は色のまま明るく。名前の下だけ紺を敷き、顔の上には重ねない */
.hp.hp-home .hx-i-over{background:linear-gradient(to top,rgba(31,45,50,.8) 0,rgba(31,45,50,.45) 45%,rgba(31,45,50,0) 75%)}
.hp.hp-home .hx-i-photo>img{filter:none;object-position:50% 20%}
.hp.hp-home .hx-band{background:var(--w-blush-2)}
.hp.hp-home .hx-band-shade::after{background:linear-gradient(to top,rgba(31,45,50,.74),rgba(31,45,50,.2) 62%,rgba(31,45,50,0) 80%)}
`;

/* ヘッダーとフッター (.hp の外)。トップの時だけ、上の黒い帯を桜色の地に */
export const HP_HOME_WOMEN_CHROME = `
html:has(.hp-home:not(.hp-line)) .rb-bar{background:linear-gradient(90deg,#F3C9CF 0,#FBE7E4 50%,#D6ECEF 100%);color:#1F2D32}
html:has(.hp-home:not(.hp-line)) .rb-tag{background:#fff;color:#B05A60;border-radius:999px}
html:has(.hp-home:not(.hp-line)) .rb-dot{background:#C2504B}
html:has(.hp-home) :is(.gh-bar,.gh-drawer,.gh-secrow){--gh-ink:#333333;--gh-sub:#6E6D6D;--gh-blue:#C2504B;--gh-wash:#FBF7F7;--gh-focus:#C2504B}
html:has(.hp-home) .gh-bar{border-bottom-color:#EADCDC}
html:has(.hp-home) .gh-dhead{border-bottom-color:#EADCDC}
html:has(.hp-home) .gh-drawer{border-right-color:#EADCDC}
html:has(.hp-home) .gh-tag{border-left-color:#EADCDC}
html:has(.hp-home) .gh-cta .bg-\\[\\#FFE566\\]{background:#C2504B;color:#fff;border-radius:999px!important;box-shadow:0 3px 0 #973A36;--icon-fill:#C2504B}
html:has(.hp-home) .gh-cta .bg-\\[\\#FFE566\\]:hover{background:#AD4743}
`;

/* 明るくする (案 B3 見せ場を絞る、2026-10-19 代表の選択)。
   グラデは最初の画面 (上の FV のまま)、作るもの (#projects)、締めの相談 (#consult) の 3 か所だけ濃いめに。
   間の節は白とごく淡い地、カードは影をやめて細い線。紺の全幅の帯とフッターは明るくして、中の白い字と線は紺に戻す。
   ボタンの赤と LINE の緑は変えない。v2 だけに効く (v1 はこの CSS を読まない)。色は TOKENS の ground、grad、band の名前を使う */
const BRIGHT = `
.hp.hp-home{background:linear-gradient(180deg,#FEFCFD 0%,#F8F2F2 100%)}

/* 地。間の節は白と淡い地を交互に。紺の帯は淡い地に罫を引く */
.hp.hp-home .hx-sec:is(.hx-white,.hx-cream,.hx-wash,.hx-yellow),.hp.hp-home .em{background:var(--w-bg)!important}
.hp.hp-home .hx-sec:is(.hx-white,.hx-cream,.hx-wash,.hx-yellow):nth-of-type(even),.hp.hp-home .em:nth-of-type(even){background:var(--w-ground-alt)!important}
.hp.hp-home :is(.hx-dark,.hx-blue,.bp,.hx-pband){background:var(--w-ground-alt)!important;color:var(--w-head);border-top:1px solid var(--w-ground-line);--focus-ring:var(--w-cta)}
.hp.hp-home #projects{background:var(--w-show-grad)!important;border-top:0}
.hp.hp-home #consult{background:linear-gradient(120deg,rgba(243,206,215,.93) 0%,rgba(230,212,231,.9) 52%,rgba(208,222,240,.93) 100%),url(/assets/stock/as1705386134-grad.webp) center/cover,var(--w-grad-pink)!important;border-top:0}
.hp.hp-home .hx-end{background:var(--w-end-wash),url(/assets/stock/as1705386134-grad.webp) center/cover,var(--w-band-edge)!important}
.hp.hp-home .hx-band{background:#F4E8EE}

/* 札と箱は白。間の節は影でなく細い線、作るものの札だけ影で浮かせる */
.hp.hp-home :is(.em-card,.hx-wyg-card,.hx-wyg-list,.pp-pay-card,.pp-sim,.pp-pay-note,.hx-vc,.fq-item,.hx-i-crit-l li,.px-pc,.hx-frame,.hx-pd-shot,.hx-qi,.pp-pt-frame,.pp-vs-frame,.hx-grant,.hx-scope-cards li,.vw-c){background-color:#fff}
.hp.hp-home :is(.em-card,.hx-wyg-card,.hx-wyg-list,.pp-pay-card,.pp-sim,.pp-pay-note,.hx-vc,.fq-item,.hx-i-crit-l li,.hx-frame,.hx-pd-shot,.hx-qi,.pp-pt-frame,.pp-vs-frame,.hx-grant,.hx-scope-cards li,.vw-c){box-shadow:none!important;border:1px solid var(--w-rule)!important}
/* 投稿の壁の札は、乗せたら枠を桜色にして押せることを見せる */
.hp.hp-home .vw-c:hover{border-color:var(--w-rose-soft)!important}
.hp.hp-home .px-pc{box-shadow:0 10px 30px rgba(141,144,184,.2)!important;border-color:rgba(234,220,220,.7)!important}
.hp.hp-home #projects .px-pc{border:0!important}
.hp.hp-home .fq-item{border-radius:var(--w-r);padding-inline:16px;margin-top:12px}

/* 紺で塗っていた小さな面は白いカードに */
.hp.hp-home :is(.em-b-base,.em-do .em-it,.em-lg-all,.hx-kd-h,.hx-pb-a,.pp-pay-tag,.pp-pt thead th.yes,.pp-sim-h,.pp-vs thead th.yes,.hx-lv-b,.hx-lv-a,.hx-lv-p){background:#fff!important;color:var(--w-head)!important;box-shadow:0 6px 18px rgba(141,144,184,.16)}
.hp.hp-home :is(.em-b-base,.em-do .em-it,.em-lg-all,.hx-kd-h,.hx-pb-a,.pp-pay-tag,.pp-pt thead th.yes,.pp-sim-h,.pp-vs thead th.yes,.hx-lv-b,.hx-lv-a,.hx-lv-p) :not(.hx-pill):not(.hx-pill *){color:var(--w-head)!important}
.hp.hp-home .hx-scope .hx-scope-tags i{color:var(--w-band-dim)!important}
.hp.hp-home .hx-scope-tr{background:transparent}
.hp.hp-home .hx-wyg-boxin{background:#FBEFF2!important;color:var(--w-head)}
.hp.hp-home .hx-wyg-boxin :is(p,span,b):not(.hx-pill *){color:var(--w-head)!important}
.hp.hp-home .hx-wyg-bk{color:var(--w-rose-ink)!important}
.hp.hp-home :is(.hx-pd-tab[aria-current=true],.pp-sim-seg [aria-checked="true"]){background:var(--w-rose-ink)!important;color:#fff!important}
.hp.hp-home :is(.hx-pd-tab[aria-current=true],.pp-sim-seg [aria-checked="true"]) *{color:#fff!important}
.hp.hp-home .hx-pb-arrow{border-left-color:#fff}

/* 帯の中の字は紺。押す札、写真の上の字、LINE の札は除く */
.hp.hp-home :is(.hx-dark,.hx-blue,.bp,.hx-pband) :is(h2,h3,h4,p,li,span,b,strong,i,small,bdi,dt,dd,a,div,figcaption,em,mark,label):not(.hx-pill):not(.hx-pill *):not(.hx-flag):not(.hx-flag *):not(.px-grp-tag):not(.card-open-hint):not(.card-open-hint *):not([class*="bg-[#06C755]"]):not([class*="bg-[#06C755]"] *):not(.hx-end-pic *):not(.hx-i-over *):not(.hx-pdt-plate *):not(.bgp):not(.bgp *){color:var(--w-head)!important}
.hp.hp-home :is(.hx-dark,.hx-blue,.bp,.hx-pband) :is(.hx-h2,h2,.bp-h,.hx-pband-h){color:var(--w-head)!important}
/* 添えの字 (白の .66〜.78 だった) は少し淡い紺 */
.hp.hp-home :is(.npb-note,.npb-note *,.npb-cap,.hx-pband-note,.hx-pband-note *,.hx-end-note,.hx-end-note *,.bp-m30-note,.bp-home-src,.bp-home-end,.hx-note,.hx-end-more,.hx-pdt-cond){color:var(--w-band-dim)!important}
.hp.hp-home .bp{--bp-dim:var(--w-band-dim);--bp-rule:var(--w-band-rule);--bp-y:var(--w-mark)}
.hp.hp-home :is(.hx-dark,.hx-blue) .hx-k{color:var(--w-rose-ink)!important}
.hp.hp-home :is(.hx-dark,.hx-blue) .hx-flag{background:var(--w-rose-ink);color:#fff}
.hp.hp-home :is(.hx-dark,.hx-blue) .hx-flag *{color:#fff}
.hp.hp-home :is(.hx-dark,.hx-blue) :is(.hx-hl,.px-em){background:linear-gradient(transparent 60%,var(--w-mark) 60%)}
.hp.hp-home .px-grp-tag{color:#fff!important}

/* 白い線と白い面 */
.hp.hp-home :is(.hx-pdt-pts,.hx-pdt-pts li,.hx-end-sign,.hx-end-pic,.hx-dark .hx-frame,.hx-dark .hx-frame-bar){border-color:var(--w-band-rule)!important}
.hp.hp-home .hx-end-step{background:#fff;border-color:var(--w-rule);box-shadow:0 6px 20px rgba(160,96,104,.10)}
.hp.hp-home .hx-end-pic{background:var(--w-blush-2)}
.hp.hp-home :is(.hx-end-no:not(.hx-end-no-line),.hx-end-opt){border-color:var(--w-rose-ink)!important;color:var(--w-rose-ink)!important;background:#fff}
.hp.hp-home .hx-end-no-line{background:#fff}
.hp.hp-home .hx-end-step::after{color:var(--w-rose-ink)!important}
.hp.hp-home .bp>.bgp,.hp.hp-home .bp>.bgp *{color:rgba(49,95,120,.06)!important;border-color:rgba(49,95,120,.08)!important}

/* 白抜きだった署名の字とロゴを元の色に */
.hp.hp-home :is(.bp-sign,.hx-sign) span{color:var(--w-head)!important}
.hp.hp-home :is(.bp .bp-sign img,.hx-sign img){filter:none}

/* 図。白い四角は紺とローズに。100 人の図の白い人と輪郭は紺に */
.hp.hp-home .bp-m30 .bp-m30-c{background:var(--w-navy)}
.hp.hp-home .bp-m30 .bp-m30-c.is-after{background:#fff;border-color:var(--w-rose-ink)}
.hp.hp-home .bp-m30 :is(.bp-m30-l,.bp-m30-l *){color:var(--w-head)!important}
.hp.hp-home .bp-home-g [fill="#fff"]{fill:var(--w-navy)}
.hp.hp-home .bp-home-g [stroke="rgba(255,255,255,.5)"]{stroke:rgba(49,95,120,.42)}
.hp.hp-home .bp-home{--bp-y:#D07C7C;--yellow:#D07C7C}

/* 読みやすさ。薄かった字 (4.5 未満) を濃く */
.hp.hp-home .em-no{color:#fff}

/* 写真の端は紺でなく地の色へ溶かす。締めの写真の下端は色を重ねず、写真そのものを透かして地へ (継ぎ目を出さない) */
.hp.hp-home .hx-pband-photo::after{background:linear-gradient(to right,var(--w-band-edge) 0,rgba(246,227,231,.55) 14%,rgba(246,227,231,0) 36%)!important}
.hp.hp-home .hx-end-photo::after{background:linear-gradient(180deg,rgba(243,206,215,0) 45%,rgba(240,209,220,.95) 100%)!important}
.hp.hp-home .hx-pband-credit{color:#fff!important;text-shadow:0 0 4px rgba(0,0,0,.45)}
@media (max-width:899px){.hp.hp-home .hx-end-photo{-webkit-mask-image:linear-gradient(180deg,#000 50%,transparent 100%);mask-image:linear-gradient(180deg,#000 50%,transparent 100%)}.hp.hp-home .hx-end-photo::after{display:none!important}}
`;

export const HP_HOME_WOMEN_CSS = (TOKENS + GROUND + TYPE + MARK + SHAPE + LINES + FILLS + BUTTON + FV + PHOTO + HP_HOME_WOMEN_CHROME + BRIGHT)
  .replace(/\/\*[\s\S]*?\*\//g, "")
  .replace(/\n\s+/g, "\n");
